import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { evCommandSchema } from "./commands.js";
import { evChargeComponentSchema, evIdentificationSchema, evInvoiceStatusSchema, evLimitReasonSchema, evPaymentMethodSchema, evPaymentStatusSchema, evSessionPhaseSchema, evSessionSourceSchema, } from "./enums.js";
/**
 * Şarj OTURUMLARI (Prompt 19, ADR 0019 §8, `docs/domain/ev.md`). Oturumu OCPP süreci (çalışma zamanı) açar, ölçer ve
 * tamamlar; API listeler, ayrıntıyı (seyreltilmiş ölçüm serisi, uygulanan güç sınırları, komutlar, tutar kalemleri, satış /
 * fatura bağı) gösterir, uzaktan durdurur ve faturayı yeniden dener. Kişisel alanlar (idTag, müşteri adı, plaka)
 * `customers.personal.view` yoksa MASKELİDİR. kWh 3 ondalık dizge; tutarlar kuruş (KDV dahil).
 */
const kwh = z.string().describe("kWh (3 ondalık dizge)");
/** `ev_sessions.price_lines` kalemi (`@station/ev-core` `priceSession` + satış ürünü). */
export const evPriceLineSchema = z.object({
    component: evChargeComponentSchema,
    quantity: z.string().describe("kWh ya da dakika (3 ondalık)"),
    unit: z.enum(["KWH", "MINUTE"]),
    unitPrice: z
        .string()
        .describe("Ortalama birim fiyat (₺, KDV dahil, 4 ondalık) — bilgi; tutar kalemin kendisidir"),
    amount: moneySchema.describe("Kuruş, KDV dahil"),
    productId: uuidSchema.nullable(),
});
export const listEvSessionsQuerySchema = pageQuerySchema
    .extend({
    stationId: uuidSchema.optional(),
    chargerId: uuidSchema.optional(),
    phase: evSessionPhaseSchema.optional(),
    paymentStatus: evPaymentStatusSchema.optional(),
    invoiceStatus: evInvoiceStatusSchema.optional(),
    source: evSessionSourceSchema.optional(),
    customerProfileId: uuidSchema.optional(),
    identification: evIdentificationSchema
        .optional()
        .describe("Tanımlama yöntemi (uygulama, kart, kasa, filo kartı…)"),
    from: localDateSchema.optional().describe("Başlangıç günü (İstanbul, dahil)"),
    to: localDateSchema.optional().describe("Bitiş günü (İstanbul, dahil)"),
})
    .refine((value) => !value.from || !value.to || value.from <= value.to, {
    message: "Bitiş başlangıçtan önce olamaz",
    path: ["to"],
});
export const evSessionSummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    source: evSessionSourceSchema,
    chargerId: uuidSchema.nullable(),
    chargerName: z.string().nullable(),
    connectorNo: z.number().int().nullable(),
    transactionId: z.number().int().nullable(),
    partnerCode: z.string().nullable(),
    partnerSessionRef: z.string().nullable(),
    identification: evIdentificationSchema,
    customerProfileId: uuidSchema.nullable(),
    customerName: z.string().nullable().describe("KVKK: `customers.personal.view` yoksa maskeli"),
    vehicleId: uuidSchema.nullable(),
    vehiclePlate: z.string().nullable().describe("KVKK: `customers.personal.view` yoksa maskeli"),
    counterpartyId: uuidSchema.nullable().describe("Filo carisi (fatura alıcısı)"),
    counterpartyName: z.string().nullable(),
    subscriptionId: uuidSchema.nullable(),
    phase: evSessionPhaseSchema,
    recovered: z.boolean(),
    startedAt: timestampSchema,
    chargingEndedAt: timestampSchema.nullable(),
    stoppedAt: timestampSchema.nullable(),
    completedAt: timestampSchema.nullable(),
    localDay: localDateSchema,
    energyKwh: kwh,
    maxPowerW: z.number().int().nullable(),
    lastPowerW: z.number().int().nullable(),
    socStart: z.string().nullable(),
    socLast: z.string().nullable(),
    stopReason: z.string().nullable(),
    chargingSeconds: z.number().int(),
    idleSeconds: z.number().int(),
    currentLimitW: z.number().int().nullable().describe("Yük yönetiminin son sınırı (W); null = sınırsız"),
    coveredKwh: kwh.describe("Abonelik hakkıyla karşılanan kWh"),
    energyAmount: moneySchema,
    timeAmount: moneySchema,
    idleAmount: moneySchema,
    totalAmount: moneySchema,
    paymentStatus: evPaymentStatusSchema,
    paymentMethod: evPaymentMethodSchema.nullable(),
    paidAt: timestampSchema.nullable(),
    posSaleId: uuidSchema.nullable(),
    invoiceStatus: evInvoiceStatusSchema,
    invoiceId: uuidSchema.nullable(),
    invoiceNo: z.string().nullable(),
    invoiceAttempts: z.number().int(),
    invoiceError: z.string().nullable(),
    /** Uyarı kodları (çalışma zamanı ve ödeme bağı; Türkçe etiketleri `EV_SESSION_WARNING_LABELS`). */
    warnings: z.array(z.string()),
    personalDataErased: z.boolean(),
});
export const evSessionPageSchema = pageOf(evSessionSummarySchema);
export const evMeterPointSchema = z.object({
    at: timestampSchema,
    energyWh: z.string().nullable(),
    powerW: z.number().int().nullable(),
    socPercent: z.string().nullable(),
});
export const evAppliedLimitSchema = z.object({
    id: uuidSchema,
    at: timestampSchema,
    method: z.enum(["EQUAL", "PRIORITY"]),
    availableW: z.number().int(),
    previousLimitW: z.number().int().nullable(),
    limitW: z.number().int(),
    suspended: z.boolean(),
    reason: evLimitReasonSchema,
    commandId: uuidSchema.nullable(),
});
export const evSessionDetailSchema = evSessionSummarySchema.extend({
    idTag: z.string().nullable().describe("KVKK: `customers.personal.view` yoksa maskeli (ilk 2 + son 2)"),
    idTokenId: uuidSchema.nullable(),
    fleetDriverId: uuidSchema.nullable(),
    meterStartWh: z.string().nullable(),
    meterStopWh: z.string().nullable(),
    lastMeterAt: timestampSchema.nullable(),
    tariffId: uuidSchema.nullable(),
    tariffName: z.string().nullable().describe("Oturum başındaki tarifenin (anlık görüntü) adı"),
    tariffSnapshot: z.record(z.string(), z.unknown()).nullable(),
    priceLines: z.array(evPriceLineSchema.extend({ productName: z.string().nullable() })),
    /** Ölçüm serisi — en çok 300 noktaya SEYRELTİLMİŞ (zaman kovası başına son örnek; ilk ve son örnek korunur). */
    meterSeries: z.array(evMeterPointSchema),
    meterSampleCount: z.number().int().describe("Seyreltilmeden önceki örnek sayısı"),
    limits: z.array(evAppliedLimitSchema),
    commands: z.array(evCommandSchema),
    posSale: z
        .object({
        id: uuidSchema,
        agentSaleId: z.string(),
        lineNo: z.number().int().nullable(),
        occurredAt: timestampSchema,
        status: z.string(),
    })
        .nullable(),
    invoice: z
        .object({
        id: uuidSchema,
        invoiceNo: z.string().nullable(),
        status: z.string(),
        scenario: z.string().nullable(),
        issueDate: localDateSchema,
        payableAmount: moneySchema,
        counterpartyId: uuidSchema,
    })
        .nullable(),
    partnerImportId: uuidSchema.nullable(),
});
/** Ödeme bağının oturuma yazdığı uyarılar (çalışma zamanının kodları `ev_sessions.warnings`'te ayrıca bulunur). */
export const EV_PAYMENT_WARNINGS = [
    "POS_AMOUNT_MISMATCH",
    "POS_DOUBLE_PAYMENT",
    "POS_SALE_VOIDED",
    "POS_PARTIAL_RETURN",
    "POS_SESSION_NOT_COMPLETED",
];
export const EV_PAYMENT_WARNING_LABELS = {
    POS_AMOUNT_MISMATCH: "Kasa satışı oturumun kalemleriyle uyuşmadı (ürün / miktar / brüt tutar); oturum ödenmemiş kaldı",
    POS_DOUBLE_PAYMENT: "Oturum kasada ikinci kez ödendi (iade / düzeltme gerekir)",
    POS_SALE_VOIDED: "Oturumu ödeyen kasa satışı iptal edildi; ödeme geri alındı (oturum yeniden ödenmeli)",
    POS_PARTIAL_RETURN: "Oturumu ödeyen satışın şarj satırı kısmen iade edildi (oturum değişmedi — elle düzeltme)",
    POS_SESSION_NOT_COMPLETED: "Tamamlanmamış oturum kasada ödendi (tutar kesinleşmemişti)",
};
//# sourceMappingURL=sessions.js.map